/**
 * ROM Utilities for Nintendo DS
 * Handles ROM file processing, hash calculation, and header parsing
 */

export interface ROMHeader {
  gameTitle: string;
  gameCode: string;
  makerCode: string;
  unitCode: number;
  deviceType: number;
  deviceCapacity: number;
  romVersion: number;
  autostart: number;
  arm9RomOffset: number;
  arm9EntryAddress: number;
  arm9RamAddress: number;
  arm9Size: number;
  arm7RomOffset: number;
  arm7EntryAddress: number;
  arm7RamAddress: number;
  arm7Size: number;
  iconTitleOffset: number;
}

export interface ROMInfo {
  hash: string;
  size: number;
  header: ROMHeader;
  isValid: boolean;
}

/**
 * Calculate SHA-256 hash of ROM file
 */
export async function calculateROMHash(file: File | ArrayBuffer): Promise<string> {
  const buffer = file instanceof File ? await file.arrayBuffer() : file;
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
}

/**
 * Read a null-terminated string from buffer
 */
function readNullTerminatedString(buffer: Uint8Array, offset: number, maxLength: number): string {
  let length = 0;
  while (length < maxLength && buffer[offset + length] !== 0) {
    length++;
  }
  return new TextDecoder('utf-8').decode(buffer.slice(offset, offset + length));
}

/**
 * Read 32-bit little-endian unsigned integer
 */
function readUInt32LE(buffer: Uint8Array, offset: number): number {
  return (
    buffer[offset] |
    (buffer[offset + 1] << 8) |
    (buffer[offset + 2] << 16) |
    (buffer[offset + 3] << 24)
  ) >>> 0;
}

/**
 * Parse Nintendo DS ROM header
 * Reference: https://problemkaputt.de/gbatek.htm#dscartridgeheader
 */
export function parseROMHeader(buffer: ArrayBuffer): ROMHeader {
  const view = new Uint8Array(buffer);

  // DS ROM header is at offset 0x0 and is 0x200 bytes
  return {
    gameTitle: readNullTerminatedString(view, 0x00, 12),
    gameCode: readNullTerminatedString(view, 0x0c, 4),
    makerCode: readNullTerminatedString(view, 0x10, 2),
    unitCode: view[0x12],
    deviceType: view[0x13],
    deviceCapacity: view[0x14],
    romVersion: view[0x1e],
    autostart: view[0x1f],
    arm9RomOffset: readUInt32LE(view, 0x20),
    arm9EntryAddress: readUInt32LE(view, 0x24),
    arm9RamAddress: readUInt32LE(view, 0x28),
    arm9Size: readUInt32LE(view, 0x2c),
    arm7RomOffset: readUInt32LE(view, 0x30),
    arm7EntryAddress: readUInt32LE(view, 0x34),
    arm7RamAddress: readUInt32LE(view, 0x38),
    arm7Size: readUInt32LE(view, 0x3c),
    iconTitleOffset: readUInt32LE(view, 0x68),
  };
}

/**
 * Validate if buffer is a valid Nintendo DS ROM
 * Note: Validation is lenient to allow homebrew ROMs that may not have
 * a valid Nintendo logo but can still run in melonDS
 */
export function validateROM(buffer: ArrayBuffer): boolean {
  // Basic size check - must be at least 512 bytes (header size)
  if (buffer.byteLength < 0x200) {
    console.error('❌ ROM validation failed: File too small', buffer.byteLength, 'bytes');
    return false;
  }

  const view = new Uint8Array(buffer);

  // Check for Nintendo logo (CRC-16 at 0x15E should be 0xCF56)
  const logoCRC = (view[0x15e] | (view[0x15f] << 8)) >>> 0;
  const isLogoValid = logoCRC === 0xcf56;

  // Check ARM9 and ARM7 offsets are reasonable
  const arm9Offset = readUInt32LE(view, 0x20);
  const arm7Offset = readUInt32LE(view, 0x30);
  const arm9Size = readUInt32LE(view, 0x2c);
  const arm7Size = readUInt32LE(view, 0x3c);

  console.log('📋 ROM Validation Info:', {
    fileSize: buffer.byteLength,
    logoCRC: '0x' + logoCRC.toString(16).padStart(4, '0'),
    logoValid: isLogoValid,
    arm9Offset: '0x' + arm9Offset.toString(16),
    arm9Size: arm9Size,
    arm7Offset: '0x' + arm7Offset.toString(16),
    arm7Size: arm7Size,
  });

  // Lenient validation: Accept ROM if it has reasonable ARM offsets
  // Even if Nintendo logo is invalid (for homebrew)
  const hasValidOffsets =
    arm9Offset > 0 &&
    arm9Offset < buffer.byteLength &&
    arm7Offset > 0 &&
    arm7Offset < buffer.byteLength &&
    arm9Size > 0 &&
    arm7Size > 0;

  if (!hasValidOffsets) {
    console.error('❌ ROM validation failed: Invalid ARM9/ARM7 offsets or sizes');
    return false;
  }

  if (!isLogoValid) {
    console.warn('⚠️ ROM has invalid Nintendo logo (homebrew?) - attempting to load anyway');
  }

  return true; // Accept all ROMs with valid structure
}

/**
 * Process ROM file and extract all information
 */
export async function processROM(file: File): Promise<ROMInfo> {
  const buffer = await file.arrayBuffer();
  const isValid = validateROM(buffer);

  if (!isValid) {
    throw new Error('Invalid Nintendo DS ROM file');
  }

  const header = parseROMHeader(buffer);
  const hash = await calculateROMHash(buffer);

  return {
    hash,
    size: buffer.byteLength,
    header,
    isValid,
  };
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
}

/**
 * Get a friendly name for the ROM
 */
export function getROMDisplayName(header: ROMHeader): string {
  return header.gameTitle.trim() || header.gameCode || 'Unknown Game';
}
